import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
  Box,
  Paper,
  Grid,
  Stack,
  Typography,
  Button,
  Chip,
  Avatar,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  MenuItem,
  Skeleton,
  Alert,
  Divider,
  List,
  ListItem,
  ListItemText,
} from "@mui/material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import WorkIcon from "@mui/icons-material/Work";
import TimelineIcon from "@mui/icons-material/Timeline";
import AssignmentIndIcon from "@mui/icons-material/AssignmentInd";
import SwapHorizIcon from "@mui/icons-material/SwapHoriz";
import SendIcon from "@mui/icons-material/Send";
import { api } from "../../utils/api";
import { useAuth } from "../../context/AuthContext";
import CommentsWidget from "../../components/CommentsWidget";

const statusColor = (s) => {
  const x = (s || "").toLowerCase();
  if (x.includes("complete")) return "success";
  if (x.includes("progress")) return "info";
  if (x.includes("submit")) return "secondary";
  if (x.includes("audit")) return "warning";
  if (x.includes("reject")) return "error";
  return "default";
};

export default function JobView() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user, can } = useAuth();

  const [job, setJob] = useState(null);
  const [services, setServices] = useState([]);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [msg, setMsg] = useState(null);

  const [timeline, setTimeline] = useState([]);
  const [timelineOpen, setTimelineOpen] = useState(false);

  const [assignModal, setAssignModal] = useState({
    open: false,
    jobServiceId: null,
    toRole: "Team",
    toUserId: "",
    remark: "",
  });

  const [passModal, setPassModal] = useState({
    open: false,
    jobServiceId: null,
    toUserId: "",
    remark: "",
  });

  const [submitModal, setSubmitModal] = useState({
    open: false,
    jobServiceId: null,
    auditorId: "",
    remark: "",
  });

  const myRole = user?.role || "Guest";
  const myId = user?.id || null;

  const loadJob = async () => {
    try {
      const [jobRes, srvRes, usersRes] = await Promise.all([
        api.get(`/jobs/detail.php?id=${id}`),
        api.get(`/jobs/services.php?job_id=${id}`),
        api.get(`/users/list.php`),
      ]);
      setJob(jobRes.job || null);
      setServices(srvRes.services || []);
      setUsers(usersRes.users || []);
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadJob();
    // eslint-disable-next-line
  }, [id]);

  const usersByRole = (role) => users.filter((u) => u.role === role);

  const loadTimeline = async (jobServiceId) => {
    try {
      const res = await api.get(
        `/jobs/timeline.php?job_service_id=${jobServiceId}`,
      );
      setTimeline(res.timeline || []);
      setTimelineOpen(true);
    } catch (e) {
      console.error(e);
    }
  };

  const doAssign = async () => {
    const { jobServiceId, toRole, toUserId, remark } = assignModal;
    if (!toRole || !toUserId || !remark.trim()) {
      setMsg({ type: "warning", text: "Remark and assignee are required" });
      return;
    }
    try {
      await api.post("/jobs/assign.php", {
        job_service_id: jobServiceId,
        assigned_to: toUserId,
        assigned_role: toRole,
        remark,
      });
      setAssignModal({
        open: false,
        jobServiceId: null,
        toRole: "Team",
        toUserId: "",
        remark: "",
      });
      setMsg({ type: "success", text: "Assigned successfully" });
      loadJob();
    } catch (e) {
      setMsg({ type: "error", text: e.message || "Assign failed" });
    }
  };

  const doPass = async () => {
    const { jobServiceId, toUserId, remark } = passModal;
    if (!toUserId || !remark.trim()) {
      setMsg({ type: "warning", text: "Remark and target user required" });
      return;
    }
    try {
      await api.post("/jobs/pass_service.php", {
        job_service_id: jobServiceId,
        to_user_id: toUserId,
        remark,
      });
      setPassModal({
        open: false,
        jobServiceId: null,
        toUserId: "",
        remark: "",
      });
      setMsg({ type: "success", text: "Passed successfully" });
      loadJob();
    } catch (e) {
      setMsg({ type: "error", text: e.message || "Pass failed" });
    }
  };

  const doSubmit = async () => {
    const { jobServiceId, auditorId, remark } = submitModal;
    if (!auditorId || !remark.trim()) {
      setMsg({ type: "warning", text: "Remark and auditor required" });
      return;
    }
    try {
      await api.post("/jobs/submit_service.php", {
        job_service_id: jobServiceId,
        auditor_id: auditorId,
        remark,
      });
      setSubmitModal({
        open: false,
        jobServiceId: null,
        auditorId: "",
        remark: "",
      });
      setMsg({ type: "success", text: "Submitted to auditor" });
      loadJob();
    } catch (e) {
      setMsg({ type: "error", text: e.message || "Submit failed" });
    }
  };

  if (loading || !job) {
    return (
      <Box>
        <Skeleton variant="rectangular" height={80} sx={{ mb: 2 }} />
        <Skeleton variant="rectangular" height={400} />
      </Box>
    );
  }

  return (
    <Box>
      <Stack
        direction={{ xs: "column", sm: "row" }}
        alignItems={{ sm: "center" }}
        justifyContent="space-between"
        spacing={2}
        sx={{ mb: 3 }}
      >
        <Stack direction="row" alignItems="center" spacing={2}>
          <Avatar sx={{ bgcolor: "primary.main", width: 48, height: 48 }}>
            <WorkIcon />
          </Avatar>
          <Box>
            <Typography variant="h5" sx={{ fontWeight: 800 }}>
              Job #{job.id}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {job.client_name} · {job.source === "cron" ? "Auto (CRON)" : "Manual"}
            </Typography>
          </Box>
        </Stack>
        <Button
          startIcon={<ArrowBackIcon />}
          variant="outlined"
          onClick={() => navigate(-1)}
        >
          Back
        </Button>
      </Stack>

      {msg && (
        <Alert severity={msg.type} sx={{ mb: 2 }} onClose={() => setMsg(null)}>
          {msg.text}
        </Alert>
      )}

      <Paper sx={{ p: 3, mb: 3 }}>
        <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 2 }}>
          Job Information
        </Typography>
        <Grid container spacing={2}>
          <Grid item xs={12} sm={6} md={3}>
            <Typography variant="caption" color="text.secondary">
              Client
            </Typography>
            <Typography variant="body2" sx={{ fontWeight: 600 }}>
              {job.client_name}
            </Typography>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Typography variant="caption" color="text.secondary">
              Total Price
            </Typography>
            <Typography variant="body2" sx={{ fontWeight: 600 }}>
              ₹{job.total_price || 0}
            </Typography>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Typography variant="caption" color="text.secondary">
              Created
            </Typography>
            <Typography variant="body2">{job.created_at || "—"}</Typography>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Typography variant="caption" color="text.secondary">
              Status
            </Typography>
            <Box sx={{ mt: 0.5 }}>
              <Chip
                size="small"
                label={job.status || "Pending"}
                color={statusColor(job.status)}
              />
            </Box>
          </Grid>
          {job.description && (
            <Grid item xs={12}>
              <Typography variant="caption" color="text.secondary">
                Description
              </Typography>
              <Typography variant="body2">{job.description}</Typography>
            </Grid>
          )}
        </Grid>
      </Paper>

      <Paper sx={{ p: 3 }}>
        <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 2 }}>
          Services
        </Typography>
        <Box sx={{ overflowX: "auto" }}>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Service</TableCell>
                <TableCell>Price</TableCell>
                <TableCell>Assigned To</TableCell>
                <TableCell>Status</TableCell>
                <TableCell align="center">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {services.length === 0 && (
                <TableRow>
                  <TableCell colSpan={5} align="center" sx={{ py: 6 }}>
                    <Typography variant="body2" color="text.secondary">
                      No services in this job
                    </Typography>
                  </TableCell>
                </TableRow>
              )}
              {services.map((s) => (
                <TableRow key={s.id} hover>
                  <TableCell sx={{ fontWeight: 600 }}>
                    {s.service_name}
                  </TableCell>
                  <TableCell>₹{s.price || 0}</TableCell>
                  <TableCell>
                    {s.assigned_to_name ? (
                      <Typography variant="body2" sx={{ fontWeight: 600 }}>
                        {s.assigned_to_name}
                      </Typography>
                    ) : (
                      <Typography variant="caption" color="text.disabled">
                        Not assigned
                      </Typography>
                    )}
                  </TableCell>
                  <TableCell>
                    <Chip
                      size="small"
                      label={s.status || "Pending"}
                      color={statusColor(s.status)}
                    />
                  </TableCell>
                  <TableCell align="center">
                    <Stack
                      direction="row"
                      spacing={0.5}
                      justifyContent="center"
                    >
                      <IconButton
                        size="small"
                        title="Timeline"
                        onClick={() => loadTimeline(s.id)}
                      >
                        <TimelineIcon fontSize="small" />
                      </IconButton>
                      {can("jobs.assign") && (
                        <IconButton
                          size="small"
                          color="primary"
                          title="Assign"
                          onClick={() =>
                            setAssignModal({
                              open: true,
                              jobServiceId: s.id,
                              toRole: "Team",
                              toUserId: "",
                              remark: "",
                            })
                          }
                        >
                          <AssignmentIndIcon fontSize="small" />
                        </IconButton>
                      )}
                      {myRole === "Team" && s.assigned_to === myId && can("desk.submit") && (
                        <>
                          <IconButton
                            size="small"
                            color="warning"
                            title="Pass"
                            onClick={() =>
                              setPassModal({
                                open: true,
                                jobServiceId: s.id,
                                toUserId: "",
                                remark: "",
                              })
                            }
                          >
                            <SwapHorizIcon fontSize="small" />
                          </IconButton>
                          <IconButton
                            size="small"
                            color="success"
                            title="Submit to Auditor"
                            onClick={() =>
                              setSubmitModal({
                                open: true,
                                jobServiceId: s.id,
                                auditorId: "",
                                remark: "",
                              })
                            }
                          >
                            <SendIcon fontSize="small" />
                          </IconButton>
                        </>
                      )}
                    </Stack>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Box>
      </Paper>

      <Box sx={{ mt: 3 }}>
        <CommentsWidget entityType="job" entityId={id} title="Job Comments" />
      </Box>

      {/* ASSIGN DIALOG */}
      <Dialog
        open={assignModal.open}
        onClose={() => setAssignModal((s) => ({ ...s, open: false }))}
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle>Assign / Reassign Service</DialogTitle>
        <DialogContent dividers>
          <Stack spacing={2} sx={{ pt: 1 }}>
            <TextField
              select
              fullWidth
              label="Assign To Role"
              value={assignModal.toRole}
              onChange={(e) =>
                setAssignModal((s) => ({
                  ...s,
                  toRole: e.target.value,
                  toUserId: "",
                }))
              }
            >
              {(myRole === "Admin" || myRole === "BDM") && [
                <MenuItem key="m" value="Manager">
                  Manager
                </MenuItem>,
                <MenuItem key="t" value="Team">
                  Team
                </MenuItem>,
                <MenuItem key="a" value="Auditor">
                  Auditor
                </MenuItem>,
              ]}
              {myRole === "Manager" && [
                <MenuItem key="t" value="Team">
                  Team
                </MenuItem>,
                <MenuItem key="m" value="Manager">
                  Manager (Self)
                </MenuItem>,
              ]}
            </TextField>
            <TextField
              select
              fullWidth
              label="Select User"
              value={assignModal.toUserId}
              onChange={(e) =>
                setAssignModal((s) => ({ ...s, toUserId: e.target.value }))
              }
            >
              <MenuItem value="">Select</MenuItem>
              {assignModal.toRole === "Manager" && myRole === "Manager" && (
                <MenuItem value={myId}>Assign to Myself</MenuItem>
              )}
              {usersByRole(assignModal.toRole).map((u) => (
                <MenuItem key={u.id} value={u.id}>
                  {u.name}
                </MenuItem>
              ))}
            </TextField>
            <TextField
              fullWidth
              multiline
              rows={3}
              label="Remark (mandatory)"
              value={assignModal.remark}
              onChange={(e) =>
                setAssignModal((s) => ({ ...s, remark: e.target.value }))
              }
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => setAssignModal((s) => ({ ...s, open: false }))}
          >
            Cancel
          </Button>
          <Button variant="contained" onClick={doAssign}>
            Assign
          </Button>
        </DialogActions>
      </Dialog>

      {/* PASS DIALOG */}
      <Dialog
        open={passModal.open}
        onClose={() => setPassModal((s) => ({ ...s, open: false }))}
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle>Pass Service to Team</DialogTitle>
        <DialogContent dividers>
          <Stack spacing={2} sx={{ pt: 1 }}>
            <TextField
              select
              fullWidth
              label="Select Team Member"
              value={passModal.toUserId}
              onChange={(e) =>
                setPassModal((s) => ({ ...s, toUserId: e.target.value }))
              }
            >
              <MenuItem value="">Select</MenuItem>
              {usersByRole("Team").map((u) => (
                <MenuItem key={u.id} value={u.id}>
                  {u.name}
                </MenuItem>
              ))}
            </TextField>
            <TextField
              fullWidth
              multiline
              rows={3}
              label="Remark (mandatory)"
              value={passModal.remark}
              onChange={(e) =>
                setPassModal((s) => ({ ...s, remark: e.target.value }))
              }
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setPassModal((s) => ({ ...s, open: false }))}>
            Cancel
          </Button>
          <Button variant="contained" color="warning" onClick={doPass}>
            Pass
          </Button>
        </DialogActions>
      </Dialog>

      {/* SUBMIT DIALOG */}
      <Dialog
        open={submitModal.open}
        onClose={() => setSubmitModal((s) => ({ ...s, open: false }))}
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle>Submit Service to Auditor</DialogTitle>
        <DialogContent dividers>
          <Stack spacing={2} sx={{ pt: 1 }}>
            <TextField
              select
              fullWidth
              label="Select Auditor"
              value={submitModal.auditorId}
              onChange={(e) =>
                setSubmitModal((s) => ({ ...s, auditorId: e.target.value }))
              }
            >
              <MenuItem value="">Select Auditor</MenuItem>
              {usersByRole("Auditor").map((u) => (
                <MenuItem key={u.id} value={u.id}>
                  {u.name}
                </MenuItem>
              ))}
            </TextField>
            <TextField
              fullWidth
              multiline
              rows={3}
              label="Remark (mandatory)"
              value={submitModal.remark}
              onChange={(e) =>
                setSubmitModal((s) => ({ ...s, remark: e.target.value }))
              }
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => setSubmitModal((s) => ({ ...s, open: false }))}
          >
            Cancel
          </Button>
          <Button variant="contained" color="success" onClick={doSubmit}>
            Submit
          </Button>
        </DialogActions>
      </Dialog>

      {/* TIMELINE DIALOG */}
      <Dialog
        open={timelineOpen}
        onClose={() => setTimelineOpen(false)}
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle>Service Timeline</DialogTitle>
        <DialogContent dividers>
          {timeline.length === 0 ? (
            <Typography variant="body2" color="text.secondary">
              No timeline records
            </Typography>
          ) : (
            <List dense>
              {timeline.map((t) => (
                <ListItem
                  key={t.id}
                  sx={{
                    border: 1,
                    borderColor: "divider",
                    borderRadius: 1,
                    mb: 1,
                  }}
                >
                  <ListItemText
                    primary={
                      <Typography variant="body2" sx={{ fontWeight: 600 }}>
                        {t.action_type}
                        {t.from_user ? ` by ${t.from_user}` : ""}
                      </Typography>
                    }
                    secondary={
                      <>
                        <Typography variant="caption" color="text.disabled">
                          {t.created_at}
                        </Typography>
                        {t.remark && (
                          <Typography variant="caption" display="block">
                            💬 {t.remark}
                          </Typography>
                        )}
                      </>
                    }
                  />
                </ListItem>
              ))}
            </List>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setTimelineOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
